<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps<{ name: 'yeti' | 'teddy' | 'pinkbean' | 'octopus' | 'cico'; active: boolean; preview?: boolean }>()
const failed = ref(false)
const sheetReady = ref(false)
const preloader = ref<HTMLImageElement>()
const sprite = ref<HTMLElement>()
const frames = computed(() => ({ yeti: 11, teddy: 8, pinkbean: 6, octopus: 5, cico: 6 })[props.name])
const asset = computed(() => new URL(`/stream-assets/${props.name}`, window.location.href).href)
let playback: Animation | undefined

function stopPlayback() {
  playback?.cancel()
  playback = undefined
}

function startPlayback() {
  stopPlayback()
  const node = sprite.value
  if (!node || !props.active || typeof node.animate !== 'function') return
  const activeDuration = Math.min(1200, Math.max(720, frames.value * 100))
  // Keep the pose timing natural, with a longer rest in both the HUD and picker.
  const cycle = 3000
  const sequence: Keyframe[] = Array.from({ length: frames.value }, (_, index) => ({
    backgroundPosition: `${index / (frames.value - 1) * 100}% 0`,
    offset: index / frames.value * activeDuration / cycle,
    easing: 'steps(1, end)',
  }))
  sequence.push(
    { backgroundPosition: '0% 0', offset: activeDuration / cycle },
    { backgroundPosition: '0% 0', offset: 1 },
  )
  // Use the element's document timeline (including PiP). Show a cycle immediately,
  // then rest. Layout probes and ordinary data refreshes must not restart it.
  playback = node.animate(sequence, { duration: cycle, iterations: Infinity })
}

watch([sprite, () => props.active, () => props.preview], startPlayback, { flush: 'post' })
watch(() => props.name, () => {
  stopPlayback()
  failed.value = false
  sheetReady.value = false
})
onMounted(() => {
  // A cached sheet can already be complete when the node is adopted into PiP.
  if (preloader.value?.complete && preloader.value.naturalWidth > 0) sheetReady.value = true
})
onBeforeUnmount(stopPlayback)
</script>

<template>
  <span v-if="!failed" class="adventure-companion" :data-motion-active="active" :data-preview="preview" aria-hidden="true">
    <img class="adventure-companion-still" :src="`${asset}.png`" alt="" draggable="false" @error="failed = true">
    <img ref="preloader" hidden :src="`${asset}-idle.png`" alt="" @load="sheetReady = true" @error="sheetReady = false">
    <span v-if="sheetReady && active" ref="sprite" class="adventure-sprite-sheet" :style="{ backgroundImage: `url(${asset}-idle.png)`, backgroundSize: `${frames * 100}% 100%` }" />
  </span>
</template>
